import { apiClient } from './apiClient';
import type { ApiResponse, PaginatedResponse, Pagination } from '../types/admin.types';

/**
 * Build query string from query object
 */
function buildQueryString(query: object): string {
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

// ============ Types ============

export type TherapistStatus = 'PENDING' | 'ACTIVE' | 'SUSPENDED' | 'REJECTED' | 'INACTIVE';

export interface TherapistListParams {
  page?: number;
  limit?: number;
  status?: TherapistStatus;
  search?: string;
  sortBy?: 'createdAt' | 'name' | 'status';
  sortOrder?: 'asc' | 'desc';
}

export interface TherapistStatusCounts {
  PENDING: number;
  ACTIVE: number;
  SUSPENDED: number;
  REJECTED: number;
  INACTIVE: number;
  total: number;
}

export interface TherapistStatusHistoryEntry {
  id: string;
  therapistId: string;
  previousStatus: TherapistStatus | null;
  newStatus: TherapistStatus;
  reason: string | null;
  notes: string | null;
  changedBy: string;
  changedByUser: {
    id: string;
    name: string;
    email: string;
  };
  createdAt: string;
}

export interface TherapistStatusHistoryResponse {
  data: TherapistStatusHistoryEntry[];
  pagination: Pagination;
}

export interface AdminTherapistDetail {
  id: string;
  userId: string;
  status: TherapistStatus;

  // User info (flattened from user table)
  email: string;
  name: string;
  phone?: string | null;
  photoUrl?: string | null;

  // Therapist profile
  bio: string;
  qualifications: string[];
  specializations: string[];
  yearsOfExperience: number;

  // Availability
  isActive: boolean;
  isAvailable: boolean;

  // Stats
  rating?: number | null;
  totalReviews: number;

  // Status metadata
  approvedAt?: string | null;
  approvedBy?: string | null;
  rejectedAt?: string | null;
  rejectedBy?: string | null;
  rejectionReason?: string | null;
  suspendedAt?: string | null;
  suspendedBy?: string | null;
  suspendedReason?: string | null;
  adminNotes?: string | null;
  lastReviewedAt?: string | null;
  lastReviewedBy?: string | null;

  // Timestamps
  createdAt: string;
  updatedAt: string;

  // Optional relations
  services?: Array<{
    serviceId: string;
    serviceName: string;
    certifiedAt?: string | null;
  }>;
}

export type AdminTherapistListItem = AdminTherapistDetail;

// ============ API Functions ============

export const adminTherapistApi = {
  /**
   * List therapists with filtering and pagination
   */
  async listTherapists(
    params: TherapistListParams = {}
  ): Promise<PaginatedResponse<AdminTherapistListItem[]>> {
    const queryString = buildQueryString(params);
    const response = await apiClient.get<PaginatedResponse<AdminTherapistListItem[]>>(
      `/api/admin/therapists${queryString}`
    );
    return response.data;
  },

  /**
   * Get a single therapist by ID with full details
   */
  async getTherapist(id: string): Promise<AdminTherapistDetail> {
    const response = await apiClient.get<ApiResponse<AdminTherapistDetail>>(
      `/api/admin/therapists/${id}`
    );
    return response.data.data;
  },

  /**
   * Get status counts for dashboard cards
   */
  async getStatusCounts(): Promise<TherapistStatusCounts> {
    const response = await apiClient.get<ApiResponse<TherapistStatusCounts>>(
      '/api/admin/therapists/counts'
    );
    return response.data.data;
  },

  /**
   * Get status history for a therapist
   */
  async getStatusHistory(
    id: string,
    page: number = 1,
    limit: number = 10
  ): Promise<TherapistStatusHistoryResponse> {
    const queryString = buildQueryString({ page, limit });
    const response = await apiClient.get<TherapistStatusHistoryResponse>(
      `/api/admin/therapists/${id}/history${queryString}`
    );
    return response.data;
  },

  /**
   * Approve a pending therapist
   */
  async approveTherapist(id: string, notes?: string): Promise<AdminTherapistDetail> {
    const response = await apiClient.post<ApiResponse<AdminTherapistDetail>>(
      `/api/admin/therapists/${id}/approve`,
      { notes }
    );
    return response.data.data;
  },

  /**
   * Reject a pending therapist
   */
  async rejectTherapist(id: string, reason: string): Promise<AdminTherapistDetail> {
    const response = await apiClient.post<ApiResponse<AdminTherapistDetail>>(
      `/api/admin/therapists/${id}/reject`,
      { reason }
    );
    return response.data.data;
  },

  /**
   * Suspend an active therapist
   */
  async suspendTherapist(
    id: string,
    reason: string,
    cancelFutureBookings: boolean = false
  ): Promise<AdminTherapistDetail> {
    const response = await apiClient.post<ApiResponse<AdminTherapistDetail>>(
      `/api/admin/therapists/${id}/suspend`,
      { reason, cancelFutureBookings }
    );
    return response.data.data;
  },

  /**
   * Reactivate a suspended or inactive therapist
   */
  async reactivateTherapist(id: string, notes?: string): Promise<AdminTherapistDetail> {
    const response = await apiClient.post<ApiResponse<AdminTherapistDetail>>(
      `/api/admin/therapists/${id}/reactivate`,
      { notes }
    );
    return response.data.data;
  },
};
